import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '@/services/api';
import { usePageTitle } from '@/shared/usePageTitle';
import { useToast } from '@/shared/toast';
import { useConfirm } from '@/shared/confirm';
import { warehouseToTxt, downloadTextFile } from '@/shared/exports';
import { ReportModal } from '@/shared/ReportModal';
import { useReportCounts } from '@/shared/reportCounts';
import { useAuth } from '@/shared/AuthContext';
import { ItemForm } from './ItemForm';
import { type Category, type Item, CATEGORIES, CATEGORY_LABEL } from './types';

interface PublicTenant { id: number; name: string }

export function WarehousePage(): React.JSX.Element {
  usePageTitle('Magazzino');
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  // Solo Magazzino+ puo' aggiungere/modificare/eliminare oggetti (richiesta Bryan).
  // Segnalazioni ed export TXT restano visibili a tutti.
  const canManage = hasRole('Magazzino', 'Amministratore', 'SuperAdmin');
  const [category, setCategory] = useState<Category | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<Item | null>(null);
  const [reportTarget, setReportTarget] = useState<{ id: string; name: string } | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const reportCounts = useReportCounts();

  const { data: counts } = useQuery({
    queryKey: ['warehouse.counts'],
    queryFn: async () => (await api.get('/warehouse/counts')).data,
  });
  const { data: items } = useQuery<Item[]>({
    queryKey: ['warehouse', category],
    queryFn: async () => (await api.get('/warehouse', { params: category ? { category } : {} })).data,
  });
  const { data: tenants } = useQuery<PublicTenant[]>({
    queryKey: ['tenants.public'],
    queryFn: async () => (await api.get('/tenants/public')).data,
  });

  const stationName = user ? tenants?.find(t => t.id === user.homeTenantId)?.name ?? '' : '';

  const del = useMutation({
    mutationFn: async (id: string) => { await api.delete(`/warehouse/${id}`); },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['warehouse'] });
      qc.invalidateQueries({ queryKey: ['warehouse.counts'] });
      toast.show('Articolo eliminato');
    },
    onError: () => toast.show("Errore durante l'eliminazione", 'error'),
  });

  async function handleDelete(id: string, name: string) {
    const ok = await confirm({
      title: 'Elimina articolo',
      message: `Sei sicuro di voler eliminare "${name}"?`,
      confirmLabel: 'Elimina', variant: 'danger',
    });
    if (ok) del.mutate(id);
  }

  function expiryClass(exp?: string): { cls: string; text: string } {
    if (!exp) return { cls: '', text: '' };
    const days = Math.round((new Date(exp).getTime() - Date.now()) / 86400000);
    const dateStr = new Date(exp).toLocaleDateString('it-IT');
    if (days < 0) return { cls: 'expired', text: `Scaduto: ${dateStr}` };
    if (days < 90) return { cls: 'expiring', text: `In scadenza: ${dateStr}` };
    return { cls: '', text: `Scadenza: ${dateStr}` };
  }

  const getCount = (c: Category) =>
    (counts ?? []).find((x: { category: string }) => x.category === c)?.count ?? 0;

  return (
    <div className="page-container">
      <header className="app-page-header">
        <div>
          <h1 className="app-page-title">🏭 Gestione Magazzino</h1>
          {stationName && <p className="app-page-subtitle">{stationName}</p>}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {canManage && (
            <button onClick={() => setShowForm(true)} className="btn-success"
              style={{ padding: '10px 20px', borderRadius: 8, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
              + Aggiungi
            </button>
          )}
          <button onClick={() => setShowExportModal(true)}
            className="btn-success"
            style={{ padding: '10px 20px', borderRadius: 8, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
            📄 Esporta TXT
          </button>
          <button onClick={() => navigate('/dashboard')} className="btn-back">← Indietro</button>
        </div>
      </header>

      <div className="warehouse-categories">
        {CATEGORIES.map(c => {
          const { icon, label } = CATEGORY_LABEL[c];
          return (
            <div key={c}
              onClick={() => setCategory(category === c ? null : c)}
              className={`warehouse-category-card ${category === c ? 'active' : ''}`}>
              <h3>{icon} {label}</h3>
              <div className="count">{getCount(c)}</div>
            </div>
          );
        })}
      </div>

      <h2 style={{ fontSize: 20, marginBottom: 15, color: '#333' }}>Materiali in Magazzino</h2>

      {(items ?? []).length === 0 && (
        <div style={{ textAlign: 'center', color: '#6c757d', padding: 40 }}>
          Nessun materiale {category && `in ${CATEGORY_LABEL[category].label}`}
        </div>
      )}

      {(items ?? []).map(i => {
        const { cls, text } = expiryClass(i.expiry);
        const { icon, label } = CATEGORY_LABEL[i.category];
        return (
          <div key={i.id} className={`warehouse-item-card ${cls}`}>
            <div className="item-header">
              <div className="item-name">{i.name}</div>
              <div className="item-actions">
                <button className="btn-warning"
                  style={{ padding: '6px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', color: '#000', position: 'relative' }}
                  onClick={() => setReportTarget({ id: i.id, name: i.name })}>
                  📋 Segnalazione
                  {reportCounts.warehouse.get(i.id) ? (
                    <span className="report-counter-badge">{reportCounts.warehouse.get(i.id)}</span>
                  ) : null}
                </button>
                {canManage && (
                  <button onClick={() => setEditingItem(i)}
                    style={{ padding: '6px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer',
                             background: 'linear-gradient(135deg, #28a745 0%, #218838 100%)', color: 'white' }}>
                    ✏️
                  </button>
                )}
                {canManage && (
                  <button onClick={() => handleDelete(i.id, i.name)}
                    style={{ padding: '6px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer',
                             background: '#dc3545', color: 'white' }}>
                    🗑️
                  </button>
                )}
              </div>
            </div>
            <div className="item-details">
              <div><strong>Categoria:</strong> {icon} {label}</div>
              <div><strong>Quantità:</strong> {i.quantity} {i.unit ?? 'pz'}</div>
              {i.serialNumber && <div><strong>Matricola:</strong> {i.serialNumber}</div>}
              {text && <div><strong>{text}</strong></div>}
            </div>
          </div>
        );
      })}

      {showForm && <ItemForm onClose={() => setShowForm(false)} />}
      {editingItem && <ItemForm item={editingItem} onClose={() => setEditingItem(null)} />}
      {reportTarget && <ReportModal target={{ warehouseItemId: reportTarget.id }}
        targetLabel={reportTarget.name} onClose={() => setReportTarget(null)} />}

      {showExportModal && (
        <div className="modal-overlay" onClick={() => setShowExportModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <h2 style={{ textAlign: 'center', margin: '0 0 8px' }}>📄 Esporta Magazzino</h2>
            <p style={{ textAlign: 'center', color: '#666', margin: '0 0 20px' }}>Seleziona il tipo di export:</p>
            <button onClick={() => {
              if (items) downloadTextFile(warehouseToTxt(items, false), `magazzino_completo_${new Date().toISOString().slice(0,10)}.txt`);
              setShowExportModal(false);
            }} style={{ width: '100%', padding: 16, marginBottom: 10, border: 'none', borderRadius: 8, cursor: 'pointer',
                        background: 'linear-gradient(135deg, #28a745 0%, #218838 100%)', color: 'white',
                        fontSize: 15, fontWeight: 600 }}>
              📦 Tutti gli oggetti
            </button>
            <button onClick={() => {
              if (items) {
                const filtered = items.filter(i => i.serialNumber && i.serialNumber.trim());
                downloadTextFile(warehouseToTxt(filtered, false), `magazzino_con_matricola_${new Date().toISOString().slice(0,10)}.txt`);
              }
              setShowExportModal(false);
            }} style={{ width: '100%', padding: 16, marginBottom: 10, border: 'none', borderRadius: 8, cursor: 'pointer',
                        background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)', color: 'white',
                        fontSize: 15, fontWeight: 600 }}>
              🔢 Solo oggetti con matricola
            </button>
            <button onClick={() => setShowExportModal(false)}
              style={{ width: '100%', padding: 12, border: 'none', borderRadius: 8, cursor: 'pointer',
                       background: '#6c757d', color: 'white', fontWeight: 600, marginTop: 8 }}>
              Annulla
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

